import React from "react";
import ReactDOM from "react-dom/client";
import {
  FluentProvider,
  webLightTheme,
  Theme,
} from "@fluentui/react-components";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  EventType,
  AccountInfo,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { loginRequest } from "./utils/authConfig";
import { initMsal } from "./utils/authConfig";
import { UseCaseProvider } from "./context/UseCaseContext/UseCaseContext";

import "./index.css";

import Layout from "./pages/layout/Layout";
import Chat from "./pages/chat/Chat";

const queryClient = new QueryClient();

const customTheme: Theme = {
  ...webLightTheme,
  colorNeutralBackground1: "#f2f2f2", // Put your specific light grey hex here
  // 1. Change Button backgrounds (Standard buttons use colorNeutralBackground1)
  // Making it transparent or white removes the default light-grey look
  colorNeutralBackground1Hover: "#e1e1e1", // Subtle hover

  // 2. Change TextArea/Input backgrounds
  // TextAreas often use colorNeutralBackground3 or colorNeutralBackgroundInverted
  colorNeutralBackground3: "white",

  // 3. For "Subtle" buttons or specific input states
  colorSubtleBackground: "transparent",
  colorSubtleBackgroundHover: "rgba(0,0,0,0.05)",
};

const AuthenticatedApp: React.FC = () => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loginInitiated, setLoginInitiated] = React.useState(false);

  React.useEffect(() => {
    // Don't attempt login if already authenticated, login is in progress, or we've already initiated login
    if (isAuthenticated || inProgress !== "none" || loginInitiated) {
      return;
    }

    // Only attempt login if user is truly not authenticated and no MSAL operations are in progress
    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
      setLoginInitiated(true);
      instance.loginRedirect(loginRequest).catch((error) => {
        console.error("Login redirect failed:", error);
        setLoginInitiated(false);
      });
    }
  }, [instance, isAuthenticated, inProgress, loginInitiated]);

  // Show loading while authentication is in progress
  if (inProgress !== "none" || (!isAuthenticated && !loginInitiated)) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#f2f2f2",
          fontFamily: "Segoe UI, sans-serif",
        }}
      >
        <div
          style={{
            padding: "2rem",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <h1 style={{ color: "#323130", marginBottom: "1rem" }}>MB Chat</h1>
          <p style={{ color: "#605e5c" }}>
            {inProgress !== "none"
              ? "Processing authentication..."
              : "Redirecting to Microsoft login..."}
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated and login hasn't been initiated
  if (!isAuthenticated) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#f2f2f2",
          fontFamily: "Segoe UI, sans-serif",
        }}
      >
        <div
          style={{
            padding: "2rem",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <h1 style={{ color: "#323130", marginBottom: "1rem" }}>MB Chat</h1>
          <p style={{ color: "#605e5c", marginBottom: "1rem" }}>
            Please sign in to continue
          </p>
          <button
            onClick={() => {
              setLoginInitiated(true);
              instance.loginRedirect(loginRequest).catch((error) => {
                console.error("Login redirect failed:", error);
                setLoginInitiated(false);
              });
            }}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#0078d4",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <FluentProvider theme={customTheme}>
      <QueryClientProvider client={queryClient}>
        <UseCaseProvider>
          <Layout />
        </UseCaseProvider>
      </QueryClientProvider>
    </FluentProvider>
  );
};

const App: React.FC<{ msalInstance: IPublicClientApplication }> = ({
  msalInstance,
}) => {
  const router = createHashRouter([
    {
      path: "/",
      element: (
        <MsalProvider instance={msalInstance}>
          <AuthenticatedApp />
        </MsalProvider>
      ),
      children: [
        {
          index: true,
          element: <Chat />,
        },
        {
          path: "*",
          lazy: () => import("./pages/NoPage"),
        },
      ],
    },
  ]);

  return (
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  );
};

// Async bootstrap
async function bootstrap() {
  const msalInstance = await initMsal();

  // Handle redirect result first
  await msalInstance.handleRedirectPromise();

  // Set active account if available
  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // Handle login success
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const account = event.payload as AccountInfo;
      msalInstance.setActiveAccount(account);
    }
  });

  ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <App msalInstance={msalInstance} />,
  );
}

bootstrap().catch(console.error);
